import React, { createContext, useContext, useEffect, useState } from "react";
import { useInterestedUser } from "../hooks/useInterestedUser";
import { OTP_METHODS, OtpMethod } from "../models/otp-methods";
import { LOGIN_METHODS, LoginMethod } from "../models/login-methods";
import { AUTH_STEPS, AuthStep } from "../models/auth-steps";
import { useClubContext } from "./ClubContext";
import { CRM_TYPES } from "../models/crm-types";
import { useConfigContext } from "./ConfigContext";
import { InterestedUser } from "../models/interested-user";

type AuthContextValue = {
	otpMethod: OtpMethod;
	setOtpMethod: React.Dispatch<React.SetStateAction<OtpMethod>>;
	otpSentMessage: string;
	setOtpSentMessage: React.Dispatch<React.SetStateAction<string>>;
	loginMethod: LoginMethod;
	setLoginMethod: React.Dispatch<React.SetStateAction<LoginMethod>>;
	interestedUser: InterestedUser | null;
	setInterestedUser: React.Dispatch<
		React.SetStateAction<InterestedUser | null>
	>;
	loadingInterestedUser: boolean;
	interestedUserError: string | null;
	authStep: AuthStep;
	setAuthStep: React.Dispatch<React.SetStateAction<AuthStep>>;
	initOtpMethod: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { club } = useClubContext();
	const { config } = useConfigContext();
	const [authStep, setAuthStep] = useState<AuthStep>(AUTH_STEPS.LOGIN);
	const [otpMethod, setOtpMethod] = useState<OtpMethod>(
		config?.language === "he" ? OTP_METHODS.SMS : OTP_METHODS.EMAIL,
	);
	const [otpSentMessage, setOtpSentMessage] = useState<string>("");
	const [loginMethod, setLoginMethod] = useState<LoginMethod>(
		LOGIN_METHODS.EMAIL,
	);
	const {
		interestedUser,
		setInterestedUser,
		isLoading: loadingInterestedUser,
		error: interestedUserError,
	} = useInterestedUser();

	const initOtpMethod = (): void => {
		setOtpMethod(
			config?.language === "he" ? OTP_METHODS.SMS : OTP_METHODS.EMAIL,
		);
	};

	// init login method
	useEffect(() => {
		if (interestedUser && !interestedUser.verified) {
			setLoginMethod(LOGIN_METHODS.GUESTID);
			return;
		}

		if (club?.cRMType === CRM_TYPES.OPTIMA && config?.language === "he") {
			setLoginMethod(LOGIN_METHODS.PASSPORT);
		}
	}, [club?.cRMType, config?.language, interestedUser]);

	return (
		<AuthContext.Provider
			value={{
				otpMethod,
				setOtpMethod,
				otpSentMessage,
				setOtpSentMessage,
				loginMethod,
				setLoginMethod,
				interestedUser,
				setInterestedUser,
				loadingInterestedUser,
				interestedUserError,
				authStep,
				setAuthStep,
				initOtpMethod,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export function useAuthContext() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuthContext must be used within a AuthProvider");
	}
	return context;
}
